import React, { useEffect, useState } from 'react';

import { createReactRenderer } from 'helpers/react-renderer';

const SERVICE_DURATION_MINUTE_MAX_LIMIT: number = 15555;

export interface ServiceDefaultProps {
  domQueryTaxClassDropdownOption: string;
  domQueryItemTypeDropdownOption: string;
  domQueryLaborDurationMinutes: string;
  domQueryServiceDefaults: string;
  totalMinutesFromEntity: number;
}

const ServiceDefaults: React.FC<ServiceDefaultProps> = ({
  domQueryTaxClassDropdownOption,
  domQueryItemTypeDropdownOption,
  domQueryLaborDurationMinutes,
  domQueryServiceDefaults,
  totalMinutesFromEntity,
}) => {
  const [showServiceDefaults, setShowServiceDefaultsShown] = useState(false);
  const [hours, setHours] = useState(Math.floor(totalMinutesFromEntity / 60));
  const [minutes, setMinutes] = useState(totalMinutesFromEntity % 60);

  useEffect(() => {
    const taxClassDropdownOption = document.querySelector(
      domQueryTaxClassDropdownOption
    ) as HTMLSelectElement;

    const itemTypeDropdownOption = document.querySelector(
      domQueryItemTypeDropdownOption
    ) as HTMLSelectElement;

    const handleTaxClassDropdownChange = (): void => {
      // This is to reflect the UI dropdown of 'Tax Class'.
      const selectedOption = taxClassDropdownOption.options[taxClassDropdownOption.selectedIndex];

      // This is to reflect the UI dropdown of 'Type'.
      const selectedItemType = itemTypeDropdownOption.options[itemTypeDropdownOption.selectedIndex];
      if (
        selectedOption.getAttribute('data-tax-type') === 'service' &&
        selectedItemType.value === 'non_inventory'
      ) {
        setShowServiceDefaultsShown(true);
      } else {
        setShowServiceDefaultsShown(false);
      }
    };

    // Add event listener to determine if the tax class changes away from data-tax-type="service".
    taxClassDropdownOption.addEventListener('change', handleTaxClassDropdownChange);

    // Add event listener to determine if the item type changes away from 'non-inventory'.
    itemTypeDropdownOption.addEventListener('change', handleTaxClassDropdownChange);

    // Check if the component should be shown to the merchant.
    handleTaxClassDropdownChange();

    return () => {
      itemTypeDropdownOption.removeEventListener('change', handleTaxClassDropdownChange);
      taxClassDropdownOption.removeEventListener('change', handleTaxClassDropdownChange);
    };
  }, [domQueryItemTypeDropdownOption, domQueryTaxClassDropdownOption]);

  useEffect(() => {
    // This is to take the gui_defs format and convert it back out into hours/minutes.
    setHours(Math.floor(totalMinutesFromEntity / 60));
    setMinutes(totalMinutesFromEntity % 60);
  }, [totalMinutesFromEntity]);

  useEffect(() => {
    const laborDurationMinutesGuiDefValue = document.querySelector(
      domQueryLaborDurationMinutes
    ) as HTMLInputElement;

    // This is to take the inputs from hour/minutes and turn them into a total minute value and assign it to the gui_defs input.
    laborDurationMinutesGuiDefValue.value = String(hours * 60 + minutes);
  }, [domQueryLaborDurationMinutes, hours, minutes]);

  useEffect(() => {
    const serviceDefaultSection = document.querySelector(domQueryServiceDefaults) as HTMLElement;

    // If the Tax Class dropdown changes, we need to determine if we show/hide the Service Defaults section.
    if (showServiceDefaults) {
      serviceDefaultSection.classList.remove('hidden');
    } else {
      serviceDefaultSection.classList.add('hidden');
    }
  }, [domQueryServiceDefaults, showServiceDefaults]);

  useEffect(() => {
    const serviceDefaultSection = document.querySelector(domQueryServiceDefaults) as HTMLElement;
    const errorMessageId = '#service-defaults-error__section';
    const saveButton = document.querySelector('#saveButton') as HTMLButtonElement;
    if (hours * 60 + minutes > SERVICE_DURATION_MINUTE_MAX_LIMIT) {
      if (!document.querySelector(errorMessageId)) {
        serviceDefaultSection.insertAdjacentHTML(
          'beforeend',
          `<div data-test="service-defaults-error__section" style="display: inline" class="service-duration-error visible" id="service-defaults-error__section">
            <span class="icon-warning-sign"></span>
            <span class="text-content">Total duration cannot exceed <span ignore>${SERVICE_DURATION_MINUTE_MAX_LIMIT}</span> minutes.</span>
          </div>`
        );
        saveButton?.setAttribute('disabled', 'true');
      }
    } else {
      const errorMessage = document.querySelector(errorMessageId) as HTMLElement;
      if (errorMessage) {
        errorMessage.remove();
        saveButton?.removeAttribute('disabled');
      }
    }
  }, [domQueryServiceDefaults, hours, minutes]);

  if (!showServiceDefaults) return null;

  return (
    <div>
      <div className="service-hours-duration__input">
        <input
          data-test="service-hours-duration__input"
          type="number"
          onFocus={(e) => {
            e.currentTarget.select();
          }}
          min="0"
          value={hours}
          onChange={(e) => {
            const newHours = parseInt(e.currentTarget.value, 10) || 0;
            setHours(newHours > 0 ? newHours : 0);
            if (window.viewFieldChanged) window.viewFieldChanged('view');
          }}
          onInput={(e) => {
            e.currentTarget.value = e.currentTarget.value.replace(/[^0-9]/g, '');
          }}
        />
      </div>
      <div className="service-minutes-duration__input">
        <input
          data-test="service-minutes-duration__input"
          type="number"
          onFocus={(e) => {
            e.currentTarget.select();
          }}
          min="0"
          value={minutes}
          onChange={(e) => {
            const newMinutes = parseInt(e.currentTarget.value, 10) || 0;
            setMinutes(newMinutes > 0 ? newMinutes : 0);
            if (window.viewFieldChanged) window.viewFieldChanged('view');
          }}
          onInput={(e) => {
            e.currentTarget.value = e.currentTarget.value.replace(/[^0-9]/g, '');
          }}
        />
      </div>
    </div>
  );
};

const RenderedComponent = createReactRenderer({
  component: ServiceDefaults,
  selector: '[data-react-root="ServiceDefaults"]',
});

history.onChangeState(
  [{ form_name: 'view', tab: 'details', id: undefined }],
  () => {
    RenderedComponent.mount();
  },
  () => {
    RenderedComponent.unmount();
  }
);

export default ServiceDefaults;
